// components/CharacterSetup.tsx — one-time identity setup before turn 1.
//
// Five optional questions:
//   1. Your gender — single select: Male / Female / Non-binary (or leave blank).
//   2. Drawn to — multi-select: Male / Female / Non-binary (or leave blank).
//   3. Your age — optional integer 17-90 (or leave blank).
//   4. Drawn to partners aged X to Y — integer 17-90 each, default 17..90.
//   5. How do you want to play? — 5-segment selector (Story / Guided /
//      Classic / Sparse / Expert). Default is Story Mode (+2) for first-
//      time players, or the player's last-used level (sticky preference).
//
// Everything is optional. "Skip — surprise me" submits with all fields
// set to null / undefined / default-range; the model uses neutral
// pronouns + picks any plausible adult age + ignores age preference.
// Skipping also keeps the currently-selected guidance level.
//
// Narrowing the drawn-to range nudges the model when it generates tracked
// companion NPCs. See server PROMPTING.md PLAYER IDENTITY.
//
// Submitting (with any combination, including all skips) sets
// player.setupComplete = true so the screen never reappears for that
// account. The post-setup edit surface lives in the user-chip menu as
// "Player identity" (PlayerIdentityEdit.tsx).

import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { styles } from "../styles";
import type { PlayerGender } from "../engine";

type DrawnGender = Exclude<PlayerGender, null>;

interface CharacterSetupProps {
  onComplete: (identity: {
    gender: PlayerGender;
    drawnTo: DrawnGender[] | null;
    /** Optional. Undefined means "no preference / surprise me." */
    age?: number;
    drawnToAgeMin: number;
    drawnToAgeMax: number;
    /**
     * Player-picked guidance level in [-2, +2]. Default is +2 (Story
     * Mode) for first-time players; subsequent stories inherit the
     * player's last-used level via App.tsx lastKnownGuidance.
     */
    guidanceLevel: number;
  }) => void;
  /**
   * Optional pre-fill for users who already have an identity from a previous
   * story. The matching pills render selected on mount; the user can still
   * adjust. When undefined, behaves exactly as on first-ever signup
   * (nothing selected, "Skip — surprise me" remains visible). When
   * `guidanceLevel` is undefined, defaults to +2 (Story Mode) — matches
   * the engine's makeInitialState default for first-time players.
   */
  defaults?: {
    gender: PlayerGender;
    drawnTo: DrawnGender[] | null;
    age?: number;
    drawnToAgeMin?: number;
    drawnToAgeMax?: number;
    guidanceLevel?: number;
  };
}

const GENDER_VALUES: DrawnGender[] = ["female", "male", "non-binary"];

const AGE_MIN = 17;
const AGE_MAX = 90;

// 5-position guidance dial, ordered high→low (Story → Expert) so the
// recommended-for-new-players option is the natural first/leftmost pick.
const GUIDANCE_LEVELS: ReadonlyArray<{ level: number; key: string }> = [
  { level: 2, key: "story" },
  { level: 1, key: "guided" },
  { level: 0, key: "classic" },
  { level: -1, key: "sparse" },
  { level: -2, key: "expert" },
];
const GUIDANCE_DEFAULT_LEVEL = 2;

function clampGuidanceLevel(v: number | undefined): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return GUIDANCE_DEFAULT_LEVEL;
  const i = Math.trunc(v);
  if (i < -2) return -2;
  if (i > 2) return 2;
  return i;
}

export default function CharacterSetup({
  onComplete,
  defaults,
}: CharacterSetupProps) {
  const { t } = useTranslation("setup");
  const GENDERS: Array<{ value: DrawnGender; label: string }> = GENDER_VALUES.map(
    (value) => ({
      value,
      label: t(
        value === "non-binary"
          ? "genders.nonBinary"
          : value === "male"
            ? "genders.male"
            : "genders.female",
      ),
    }),
  );
  // Seed from `defaults` when provided so the screen mounts with the
  // player's most-recently-seen identity already picked. UX intent: a
  // returning player shouldn't have to re-make the same choice for every
  // new story. They can still tap to clear or change selections.
  const [gender, setGender] = useState<PlayerGender>(defaults?.gender ?? null);
  const [drawnTo, setDrawnTo] = useState<DrawnGender[]>(
    defaults?.drawnTo ?? [],
  );
  // Age input is held as a string so the field can be empty (no
  // numeric default that the user has to delete first); validation
  // happens on submit. Pre-fill if defaults supplied an age.
  const [ageText, setAgeText] = useState<string>(
    typeof defaults?.age === "number" ? String(defaults.age) : "",
  );
  // Drawn-to-age min/max held as strings (same rationale as ageText above):
  // clamping on every keystroke makes mid-typing values like "2" (heading to
  // "24") snap to AGE_MIN and the field reverts — impossible to type
  // anything that starts below the floor. Validation + clamp happen on
  // submit; the user can type freely.
  const [drawnToAgeMinText, setDrawnToAgeMinText] = useState<string>(
    typeof defaults?.drawnToAgeMin === "number"
      ? String(clamp(defaults.drawnToAgeMin))
      : String(AGE_MIN),
  );
  const [drawnToAgeMaxText, setDrawnToAgeMaxText] = useState<string>(
    typeof defaults?.drawnToAgeMax === "number"
      ? String(clamp(defaults.drawnToAgeMax))
      : String(AGE_MAX),
  );
  const [ageError, setAgeError] = useState<string | null>(null);

  // Guidance level pre-fills from defaults (sticky preference). If absent,
  // first-time players land on Story Mode (+2) — matches the engine's
  // makeInitialState default so the slider and the persisted state agree.
  const [guidanceLevel, setGuidanceLevel] = useState<number>(
    clampGuidanceLevel(defaults?.guidanceLevel),
  );

  function toggleDrawnTo(g: DrawnGender) {
    setDrawnTo((cur) =>
      cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g],
    );
  }

  function parseAge(s: string): number | null {
    const trimmed = s.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return null;
    const i = Math.trunc(n);
    if (i < AGE_MIN || i > AGE_MAX) return null;
    return i;
  }

  function handleSubmit() {
    // Age is optional. Empty input → undefined (no preference). Non-empty
    // but invalid → show inline error and abort.
    const trimmed = ageText.trim();
    let age: number | undefined;
    if (trimmed) {
      const parsed = parseAge(ageText);
      if (parsed === null) {
        setAgeError(
          t("characterSetup.ageOutOfRange", { min: AGE_MIN, max: AGE_MAX }),
        );
        return;
      }
      age = parsed;
    }
    // Parse the deferred text inputs. Blank → fall back to AGE_MIN/MAX
    // (the field defaults). Non-numeric / out-of-range also falls back —
    // the clamp below normalises in any case. Silently swap reversed
    // bounds — UX is friendlier than scolding.
    const parsedMin = Number.parseInt(drawnToAgeMinText.trim(), 10);
    const parsedMax = Number.parseInt(drawnToAgeMaxText.trim(), 10);
    let min = clamp(Number.isFinite(parsedMin) ? parsedMin : AGE_MIN);
    let max = clamp(Number.isFinite(parsedMax) ? parsedMax : AGE_MAX);
    if (min > max) {
      const swap = min;
      min = max;
      max = swap;
    }
    setAgeError(null);
    onComplete({
      gender,
      drawnTo: drawnTo.length > 0 ? drawnTo : null,
      age,
      drawnToAgeMin: min,
      drawnToAgeMax: max,
      guidanceLevel,
    });
  }

  function handleSkipAll() {
    // "Surprise me" — submit everything as null / undefined / defaults. The
    // model uses neutral pronouns, picks any plausible adult age, and
    // doesn't filter companion ages by preference. Guidance level keeps
    // its current value (the dial is a UX preference, not an identity
    // signal — even a "surprise me" player picks how much hand-holding
    // they want).
    setAgeError(null);
    onComplete({
      gender: null,
      drawnTo: null,
      age: undefined,
      drawnToAgeMin: AGE_MIN,
      drawnToAgeMax: AGE_MAX,
      guidanceLevel,
    });
  }

  return (
    <div style={styles.loginShell}>
      <div style={styles.grain} />
      <div style={styles.loginCard}>
        <h1 style={styles.loginTitle}>{t("characterSetup.title")}</h1>
        <div style={styles.loginSub}>{t("characterSetup.subtitle")}</div>

        <div style={{ marginTop: 24 }}>
          <div style={styles.loginStepLabel}>{t("characterSetup.youAreLabel")}</div>
          <div style={pillRow}>
            {GENDERS.map((g) => (
              <button
                key={g.value}
                type="button"
                style={pillStyle(gender === g.value)}
                onClick={() => setGender(gender === g.value ? null : g.value)}
              >
                {g.label}
              </button>
            ))}
          </div>
          <div style={hint}>{t("characterSetup.youAreHint")}</div>
        </div>

        <div style={{ marginTop: 18 }}>
          <div style={styles.loginStepLabel}>{t("characterSetup.drawnToLabel")}</div>
          <div style={pillRow}>
            {GENDERS.map((g) => (
              <button
                key={g.value}
                type="button"
                style={pillStyle(drawnTo.includes(g.value))}
                onClick={() => toggleDrawnTo(g.value)}
              >
                {g.label}
              </button>
            ))}
          </div>
          <div style={hint}>{t("characterSetup.drawnToHint")}</div>
        </div>

        <div style={{ marginTop: 18 }}>
          <div style={styles.loginStepLabel}>{t("characterSetup.yourAgeLabel")}</div>
          <div style={ageInputRow}>
            <input
              type="number"
              min={AGE_MIN}
              max={AGE_MAX}
              step={1}
              inputMode="numeric"
              value={ageText}
              onChange={(e) => {
                setAgeText(e.target.value);
                if (ageError) setAgeError(null);
              }}
              placeholder={t("characterSetup.agePlaceholder")}
              style={ageInputStyle}
              aria-label={t("characterSetup.ageAriaLabel")}
            />
            <span style={ageUnit}>{t("characterSetup.yearsUnit")}</span>
          </div>
          <div style={hint}>{t("characterSetup.yourAgeHintFirstRun")}</div>
          {ageError && <div style={errorHint}>{ageError}</div>}
        </div>

        <div style={{ marginTop: 18 }}>
          <div style={styles.loginStepLabel}>{t("characterSetup.agePartnersLabel")}</div>
          <div style={ageRangeRow}>
            <input
              type="number"
              min={AGE_MIN}
              max={AGE_MAX}
              step={1}
              inputMode="numeric"
              value={drawnToAgeMinText}
              onChange={(e) => setDrawnToAgeMinText(e.target.value)}
              style={ageInputStyle}
              aria-label={t("characterSetup.agePartnersMinAria")}
            />
            <span style={ageRangeSep}>{t("characterSetup.ageRangeSeparator")}</span>
            <input
              type="number"
              min={AGE_MIN}
              max={AGE_MAX}
              step={1}
              inputMode="numeric"
              value={drawnToAgeMaxText}
              onChange={(e) => setDrawnToAgeMaxText(e.target.value)}
              style={ageInputStyle}
              aria-label={t("characterSetup.agePartnersMaxAria")}
            />
          </div>
          <div style={hint}>{t("characterSetup.agePartnersHint")}</div>
        </div>

        <div style={{ marginTop: 18 }}>
          <div style={styles.loginStepLabel}>{t("guidance.heading")}</div>
          <div style={hint}>{t("guidance.subheading")}</div>
          <div style={guidanceColumn}>
            {GUIDANCE_LEVELS.map((opt) => {
              const selected = opt.level === guidanceLevel;
              return (
                <button
                  key={opt.level}
                  type="button"
                  style={guidanceCardStyle(selected)}
                  onClick={() => setGuidanceLevel(opt.level)}
                  aria-pressed={selected}
                >
                  <div style={guidanceCardLabel}>
                    {t(`guidance.${opt.key}`)}
                  </div>
                  <div style={guidanceCardDesc}>
                    {t(`guidance.${opt.key}Desc`)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          style={{
            ...styles.loginPrimary,
            marginTop: 28,
          }}
          onClick={handleSubmit}
        >
          {t("characterSetup.beginCta")}
        </button>

        <div style={styles.loginRow}>
          <button
            type="button"
            style={styles.loginLink}
            onClick={handleSkipAll}
          >
            {t("characterSetup.skipSurprise")}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes flicker { 0%,100%{opacity:1} 50%{opacity:0.92} }
        @keyframes fadein { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

function clamp(v: number): number {
  if (!Number.isFinite(v)) return AGE_MIN;
  const i = Math.trunc(v);
  if (i < AGE_MIN) return AGE_MIN;
  if (i > AGE_MAX) return AGE_MAX;
  return i;
}

const pillRow: CSSProperties = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  marginTop: 8,
};

const hint: CSSProperties = {
  marginTop: 8,
  fontSize: "0.75rem",
  color: "var(--ink-muted, #7a6243)",
  fontStyle: "italic",
  fontFamily:
    "var(--font-body, 'Cormorant Garamond', Georgia, serif)",
};

const errorHint: CSSProperties = {
  ...hint,
  color: "#e08585",
  fontStyle: "normal",
};

const ageInputRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 8,
};

const ageRangeRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 8,
};

const ageRangeSep: CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.85rem",
  color: "var(--ink-muted, #a89070)",
  letterSpacing: "0.05em",
};

const ageUnit: CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.85rem",
  color: "var(--ink-muted, #a89070)",
  letterSpacing: "0.05em",
};

const ageInputStyle: CSSProperties = {
  width: 80,
  padding: "8px 12px",
  background: "transparent",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.3))",
  borderRadius: 2,
  color: "var(--ink, #e8c896)",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.9rem",
  letterSpacing: "0.05em",
  outline: "none",
  textAlign: "center",
};

const guidanceColumn: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  marginTop: 10,
};

const guidanceCardLabel: CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.85rem",
  letterSpacing: "0.05em",
  textTransform: "lowercase",
};

const guidanceCardDesc: CSSProperties = {
  marginTop: 4,
  fontFamily:
    "var(--font-body, 'Cormorant Garamond', Georgia, serif)",
  fontSize: "0.8rem",
  fontStyle: "italic",
  lineHeight: 1.35,
  color: "var(--ink-muted, #a89070)",
};

// 5-segment guidance selector. Same active/inactive treatment as the
// gender + drawn-to pills (var-based theming so each scenario tints
// the active card with its own accent token).
function guidanceCardStyle(active: boolean): CSSProperties {
  return {
    padding: "10px 14px",
    textAlign: "left",
    background: active
      ? "var(--paper, rgba(212,165,116,0.15))"
      : "transparent",
    border: `1px solid ${
      active
        ? "var(--accent, #d4a574)"
        : "var(--paper-edge, rgba(212,165,116,0.3))"
    }`,
    borderRadius: 2,
    color: active
      ? "var(--ink, #e8c896)"
      : "var(--ink-muted, #a89070)",
    cursor: "pointer",
    transition:
      "all 0.15s ease-out, background 0.4s ease-out, color 0.4s ease-out, border-color 0.4s ease-out",
  };
}

// CharacterSetup is rendered inside the in-game ScenarioThemeWrapper, so
// the pills inherit the active scenario's accent/ink/paper-edge tokens.
// Fallbacks preserve the pre-multi-scenario amber palette when the
// wrapper isn't in scope.
function pillStyle(active: boolean): CSSProperties {
  return {
    padding: "8px 16px",
    background: active
      ? "var(--paper, rgba(212,165,116,0.15))"
      : "transparent",
    border: `1px solid ${
      active
        ? "var(--accent, #d4a574)"
        : "var(--paper-edge, rgba(212,165,116,0.3))"
    }`,
    borderRadius: 2,
    color: active
      ? "var(--ink, #e8c896)"
      : "var(--ink-muted, #a89070)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.85rem",
    letterSpacing: "0.05em",
    cursor: "pointer",
    transition:
      "all 0.15s ease-out, background 0.4s ease-out, color 0.4s ease-out, border-color 0.4s ease-out",
    textTransform: "lowercase",
  };
}
